import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { API, getToken, SERVICE_STATUS_LABELS, SERVICE_STATUS_COLORS, formatApiErrorDetail } from "@/lib/api";
import { ArrowLeft, Phone, Mail, AlertTriangle, Pencil, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function DriverDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [services, setServices] = useState([]);

  const load = async () => {
    try {
      const [d, s] = await Promise.all([
        api.get(`/drivers/${id}`),
        api.get(`/drivers/${id}/services`),
      ]);
      setDriver(d.data);
      setServices(s.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [id]);

  if (!driver) return <div className="p-12 text-center text-zinc-500">Cargando…</div>;

  const photoUrl = driver.photo_path
    ? `${API}/users/${driver.id}/photo?auth=${encodeURIComponent(getToken())}`
    : null;

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-6xl mx-auto" data-testid="driver-detail-page">
      <button
        onClick={() => navigate("/users")}
        className="text-xs uppercase tracking-widest text-zinc-500 hover:text-black flex items-center gap-2 mb-6"
      >
        <ArrowLeft size={14} /> Volver a conductores
      </button>

      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 pb-6 border-b border-black">
        <div className="flex items-center gap-6">
          <div className="w-28 h-28 border-2 border-black bg-zinc-100 overflow-hidden flex items-center justify-center shrink-0">
            {photoUrl ? (
              <img src={photoUrl} alt={driver.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-4xl font-bold text-zinc-400">
                {driver.name?.slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Conductor</div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{driver.name}</h1>
            <div className="flex flex-wrap gap-4 mt-3 text-sm text-zinc-700">
              <a href={`mailto:${driver.email}`} className="inline-flex items-center gap-2 underline underline-offset-4">
                <Mail size={14} /> {driver.email}
              </a>
              {driver.phone && (
                <a href={`tel:${driver.phone}`} className="inline-flex items-center gap-2 underline underline-offset-4">
                  <Phone size={14} /> {driver.phone}
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => window.open(`${API}/drivers/${id}/dossier.pdf?auth=${encodeURIComponent(getToken())}`, "_blank")}
            className="rounded-none btn-primary h-10 text-xs uppercase tracking-widest"
            data-testid="download-dossier-btn"
          >
            <FileDown size={14} className="mr-2" /> Dossier PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/drivers/${id}/amonestaciones`)}
            className="rounded-none border-black h-10 text-xs uppercase tracking-widest"
            data-testid="goto-sanctions-btn"
          >
            <AlertTriangle size={14} className="mr-2" /> Amonestaciones
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate("/users")}
            className="rounded-none border-black h-10 text-xs uppercase tracking-widest"
          >
            <Pencil size={14} className="mr-2" /> Editar en usuarios
          </Button>
        </div>
      </header>

      {/* Fomento verification badge & extra info */}
      <div className="border border-black bg-white p-5 mb-6" data-testid="driver-fomento-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Verificación Fomento</div>
            {driver.fomento_verified ? (
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-emerald-700 text-emerald-700 bg-emerald-50">
                  Verificado
                </span>
                {driver.fomento_verified_at && (
                  <span className="text-xs text-zinc-500">
                    · {new Date(driver.fomento_verified_at).toLocaleDateString("es-ES")}
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-1">
                <span className="inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border border-[#E63946] text-[#E63946] bg-red-50">
                  Sin verificar
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm mt-4 pt-4 border-t border-zinc-200">
          <Field label="DNI/NIE" value={driver.dni} />
          <Field label="Nacimiento" value={driver.birth_date} />
          <Field label="Ciudad" value={driver.city} />
          <Field label="Permiso" value={driver.license_number} />
          <Field label="Caducidad permiso" value={driver.license_expiry} />
          <Field label="Licencia VTC/BTP" value={driver.vtc_license} />
          <Field label="Vehículo" value={`${driver.vehicle_brand || ""} ${driver.vehicle_model || ""}`.trim() || "—"} />
          <Field label="Matrícula" value={driver.vehicle_plate} highlight />
          <Field label="ITV" value={driver.itv_expiry} />
        </div>
        {driver.car_photo_path ? (
          <div className="mt-4 border border-black">
            <img
              src={`${API}/users/${id}/car-photo?auth=${encodeURIComponent(getToken())}`}
              alt="Vehículo"
              className="w-full max-h-80 object-cover"
              data-testid="car-photo-img"
            />
          </div>
        ) : (
          <div className="mt-4 text-xs text-zinc-500">
            Sin foto del vehículo. Sube una desde el menú de conductores.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="border border-black p-5 bg-white">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Servicios</div>
          <div className="font-display text-4xl font-bold mt-1" data-testid="stat-services">{driver.stats?.services || 0}</div>
        </div>
        <div className="border border-black p-5 bg-white">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Amonestaciones</div>
          <div className="font-display text-4xl font-bold mt-1" data-testid="stat-sanctions">{driver.stats?.sanctions || 0}</div>
        </div>
        <div className="border border-black p-5 bg-white">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Reclamaciones</div>
          <div className="font-display text-4xl font-bold mt-1" data-testid="stat-complaints">{driver.stats?.complaints || 0}</div>
        </div>
      </div>

      <section className="border border-black bg-white">
        <div className="border-b border-black p-4 font-display font-semibold text-sm uppercase tracking-widest">
          Servicios asignados ({services.length})
        </div>
        <div className="overflow-x-auto">
          <table className="ds-table w-full" data-testid="driver-services-table">
            <thead>
              <tr>
                <th>Reserva</th>
                <th>Cliente</th>
                <th>Fecha</th>
                <th>Origen → Destino</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {services.length === 0 ? (
                <tr><td colSpan={5} className="text-sm text-zinc-500 py-6 text-center">Sin servicios asignados.</td></tr>
              ) : services.map((s) => {
                const color = SERVICE_STATUS_COLORS[s.status] || "#0A0A0A";
                return (
                  <tr key={s.id}>
                    <td className="font-mono font-semibold">
                      <Link to={`/services/${s.id}`} className="hover:underline">{s.booking_number}</Link>
                    </td>
                    <td>{s.client_name}</td>
                    <td>{s.pickup_date} · {s.pickup_time}</td>
                    <td className="text-sm truncate max-w-xs">{s.pickup_location} → {s.dropoff_location}</td>
                    <td>
                      <span
                        className="inline-flex items-center gap-2 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest border"
                        style={{ borderColor: color, color }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
                        {SERVICE_STATUS_LABELS[s.status]}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, highlight }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={highlight ? "font-mono text-sm font-bold" : "text-sm"}>{value || "—"}</div>
    </div>
  );
}
